import axios from 'axios';
import 'firebase/auth';
import queryString from 'query-string';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import BookingAPI from '../../api/booking.api';
import { bugsnagNotify } from '../../bugsnag';
import { BookingSourceType } from '../../enums/BookingSourceType';
import { BookingProcessStoreContext } from '../../stores/BookingProcessStore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import ReCAPTCHA from 'react-google-recaptcha';
import {
    trackDineout,
    trackDineoutDiscovery,
} from '@dineouticeland/do-tracking';
import { useCompanyIdentifier } from '../../contexts/TrackingContext';
import HeaderOLD from '../../components/OLD_Header.tsx';
import Footer from '../../components/Footer.tsx';
import { Spinner } from '../TA/components/Loader/Spinner.tsx';

const ReserveStyled = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

const Style = styled.div`
    padding: 16px;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    min-height: calc(100vh - 350px);
    padding-top: 100px;

    > div.unsuccesful-message {
        margin-bottom: 24px;
    }
    > div.btn-container {
        width: 240px;
    }
`;

const bookingAPI = new BookingAPI();

const Reserve: FC<RouteComponentProps> = ({ history }) => {
    const BookingProcessStore = useContext(BookingProcessStoreContext);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const recaptchaSiteKey = import.meta.env.VITE_INVISIBLE_RECAPTCHA_SITE_KEY;
    const [recaptchaToggled, setRecaptchaToggled] = useState(false);

    const { restaurantId } = queryString.parse(window.location.search);

    // Set company identifier for restaurant-level tracking (cleared on unmount).
    useCompanyIdentifier(restaurantId as string);

    useEffect(() => {
        trackDineoutDiscovery('Reservation Step Viewed', { step: 'reserve' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const menus: Array<{ menuId: number; count: number }> = useMemo(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { menuSelection }: { menuSelection: string | undefined } =
            queryString.parse(history.location.search);
        if (!menuSelection) {
            return [];
        }
        return menuSelection.split(',').map((ms) => {
            const selection = ms.split(';');
            return {
                menuId: parseInt(selection[0], 10),
                count: parseInt(selection[1], 10),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const reserve = async (captchaToken: string) => {
        const {
            dateTime,
            guests,
            restaurantId,
            eventId,
            isolation,
            isWaitingList,
        } = queryString.parse(window.location.search);
        const waitingListWhenFullyBooked = Boolean(
            isWaitingList &&
                (isWaitingList as string)?.toLowerCase() === 'true',
        );
        const sourceType =
            isolation === 'true'
                ? BookingSourceType.Portal
                : BookingSourceType.Web;
        const cancelToken = axios.CancelToken.source();

        const r = (token: string) => {
            bookingAPI
                .reserve(
                    dateTime as string,
                    guests as unknown as number,
                    restaurantId as unknown as number,
                    eventId as unknown as number,
                    sourceType,
                    menus,
                    waitingListWhenFullyBooked,
                    cancelToken,
                    token,
                    captchaToken ?? '',
                )
                .then((res) => {
                    const { success, guid } = res.data;
                    if (success) {
                        trackDineoutDiscovery(
                            'Reservation Redirected To Checkout',
                            {
                                target: 'checkout',
                            },
                        );
                        BookingProcessStore.nextStep(undefined, guid);
                    } else {
                        trackDineout('Reservation Failed', {
                            type: 'reserve',
                            reason: 'Failed to reserve on reserve endpoint 1',
                        });
                        BookingProcessStore.error();
                    }
                })
                .catch((e) => {
                    trackDineout('Reservation Failed', {
                        type: 'reserve',
                        reason: 'provider_error',
                    });

                    if (!axios.isCancel(e)) {
                        bugsnagNotify(e);
                        if (e.response.status === 429) {
                            history.goBack();
                        } else {
                            throw new Error(
                                'Error reserving. Please try again.',
                            );
                        }
                    }
                });
        };

        // Force refresh token and reserve
        // Force refresh is to minimize likelihood of token being expired when reaching API
        // (unlikely but could happen)
        const authThenContinue = async () => {
            try {
                const auth = getAuth();

                let user = auth.currentUser;

                if (!user) {
                    user = (await signInAnonymously(auth)).user;
                }

                const token = await user!.getIdToken(true);
                r(token);
            } catch (e) {
                bugsnagNotify(e, (event) => {
                    event.addMetadata('meta', {
                        isOnline: navigator.onLine,
                        userAgent: navigator.userAgent,
                    });
                    event.addMetadata('bookingContext', {
                        restaurantId,
                        eventId,
                        guests,
                        dateTime,
                        isWaitingList,
                    });
                });
                Promise.reject(e);
            }
        };

        authThenContinue();

        return () => {
            cancelToken.cancel();
        };
    };

    useEffect(() => {
        recaptchaRef.current?.execute();
    }, [recaptchaRef]);

    return (
        <>
            <Style>
                <HeaderOLD inverted />
                <Container>
                    <ReserveStyled>
                        <Spinner />
                        {!recaptchaToggled && (
                            <div>
                                <ReCAPTCHA
                                    ref={recaptchaRef}
                                    sitekey={recaptchaSiteKey}
                                    size="invisible"
                                    onChange={(token) => {
                                        if (token) {
                                            setRecaptchaToggled(true);
                                            reserve(token);
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </ReserveStyled>
                </Container>
            </Style>
            <Footer />
        </>
    );
};

export default Reserve;
